import styled from '@emotion/styled';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/repository/axios';
import { isSalaryUnavailable, SalarySurveyCurrentResponse } from '@/api/repository/types/types';
import { formatPercentileTop } from './salarySurveyOptions';

/**
 * 급여 실태조사 진입 규칙(2026-11-01) — 마이페이지·햄버거 메뉴·랜딩·설문·리포트·내 급여 위치가 같이 쓴다.
 *
 * - 참여는 한 해(1~12월, 달력 연도)에 한 번: 올해 응답이 있으면 [급여 실태조사 참여] 대신 [내 급여 위치 보기]·[응답 고치기].
 *   이직·급여 변경은 새 응답이 아니라 최신 응답을 고친다(서버가 같은 문서를 고쳐 지금 회차로 옮긴다). 다음 해 1월 1일부터 다시 참여한다.
 * - 새 리포트 N: 가장 최근 발행 리포트를 아직 안 봤으면(hasUnseenReport) 링크 옆에 빨간 원 N.
 */

/** 올해(1~12월) 응답한 회원인지 — 옛 서버(respondedThisYear 없음)는 이번 회차 응답으로 판정 */
export const respondedSalaryThisYear = (current: SalarySurveyCurrentResponse | null | undefined): boolean =>
    !!current?.respondedThisYear || !!current?.myResponse;

/** 다시 새로 참여할 수 있는 달 — "2027년 10월". 값이 없거나 깨졌으면 빈 문자열 */
export const formatNextEligibleMonth = (raw: string | null | undefined): string => {
    if (!raw) return '';
    const date: Date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
};

/** 응답을 고친 뒤 띄우는 안내 */
export const REVISED_TOAST_MESSAGE = '고친 내용은 다음 발행부터 반영돼요';

/** 응답 고치기 안내 문구 — 설문·내 급여 위치가 같은 문장을 쓴다 */
export const REVISE_PROMPT = '이직했거나 급여가 바뀌었다면 응답을 고쳐 주세요';

/** 이미 공개된 결과는 그대로라는 안내 — 문장마다 줄바꿈(pre-line 자리에서만 쓴다) */
export const REVISE_EFFECT_NOTE = '고친 내용은 다음 발행부터 반영돼요.\n이미 공개된 리포트와 내 급여 위치는 바뀌지 않아요.';

/**
 * 리포트 본 처리 — 공개 리포트·내 급여 위치가 결과를 보여 줄 때 부른다(N 을 끈다).
 * 부가 기능이라 실패해도 화면을 막지 않는다(다음에 다시 보면 다시 부른다).
 */
export const markSalaryReportSeen = (campaignKey: string | null | undefined): void => {
    if (!campaignKey) return;
    api.salarySurvey.markReportSeen(campaignKey).catch((error: unknown) => {
        console.debug('리포트 본 처리 실패:', error);
    });
};

/**
 * 새 리포트 N — 헤더 알림 종 배지(Header.tsx BellBadge)와 같은 16px·#e24b4a·흰 10px 700(시안 (가) 빨간 원).
 * 글자 N 대신 화면 낭독기에는 '새 리포트 있음'을 읽힌다.
 */
const NewBadgeCircle = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 16px;
    height: 16px;
    margin-left: 4px;
    border-radius: 50%;
    background: #e24b4a;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
`;

export const NewReportBadge = () => (
    <NewBadgeCircle role="img" aria-label="새 리포트 있음">
        <span aria-hidden="true">N</span>
    </NewBadgeCircle>
);

/**
 * '내 활동' 급여 줄 하나 — 햄버거 메뉴(MobileDrawerMenu)와 마이페이지(UserMyPage, 하단 MY·PC 공용)가 **같은 목록**을 그린다.
 * 줄 구성·문구·주소·N 은 [buildSalaryEntryLinks] 한 곳에서만 정한다(두 화면이 다시 어긋나지 않게, 2026-11-01).
 *
 * @property value 오른쪽 보조 값(예: '상위 12%') — 없으면 null
 * @property badge 새 리포트 N 을 붙일지
 */
export interface SalaryEntryLink {
    key: 'position' | 'join' | 'newReport';
    label: string;
    path: string;
    value: string | null;
    badge: boolean;
}

/**
 * 급여 줄 목록 — 올해 응답했으면 [내 급여 위치](상위 %·새 리포트 N),
 * 아니면 [급여 실태조사 참여] + 안 본 새 리포트가 있으면 [새 급여 리포트 보기] N.
 */
export const buildSalaryEntryLinks = (
    survey: SalarySurveyCurrentResponse | null | undefined,
    percentileTop: number | null | undefined,
): SalaryEntryLink[] => {
    const responded: boolean = respondedSalaryThisYear(survey);
    const hasUnseen: boolean = !!survey?.hasUnseenReport;
    const percentileText: string | null = percentileTop != null ? `상위 ${formatPercentileTop(percentileTop)}%` : null;
    if (responded) {
        return [{ key: 'position', label: '내 급여 위치', path: '/salary-surveys/results/me', value: percentileText, badge: hasUnseen }];
    }
    const links: SalaryEntryLink[] = [
        { key: 'join', label: '급여 실태조사 참여', path: '/salary-surveys/response', value: null, badge: false },
    ];
    // 응답 안 한 회원에게도 안 본 새 리포트는 알린다
    if (hasUnseen && survey?.latestReportKey) {
        links.push({ key: 'newReport', label: '새 급여 리포트 보기', path: `/salary-surveys/reports/${survey.latestReportKey}`, value: null, badge: true });
    }
    return links;
};

/**
 * 급여 현황 + 상위 % 조회 — 햄버거 메뉴·마이페이지 공용.
 *
 * - [loadCurrent] 가 true 가 될 때마다 현황을 다시 읽는다(리포트를 본 뒤 N 이 남지 않게). 실패하면 null(참여 줄만 보인다).
 * - 상위 % 는 올해 응답 + 면허 인증([canLoadBaseline])일 때만, 이 화면이 살아 있는 동안 한 번만 부른다.
 */
export const useSalaryEntry = ({ loadCurrent, canLoadBaseline }: { loadCurrent: boolean; canLoadBaseline: boolean }) => {
    const [survey, setSurvey] = useState<SalarySurveyCurrentResponse | null>(null);
    // 메뉴의 상위 % — 내 급여 위치 화면 맨 위 숫자와 같아야 한다: 올해 결과가 있으면 그것, 없으면 작년 조사 기준
    const [percentileTop, setPercentileTop] = useState<number | null>(null);
    const baselineRequested = useRef<boolean>(false);

    useEffect(() => {
        if (!loadCurrent) return undefined;
        let cancelled = false;
        api.salarySurvey
            .getCurrent()
            .then((response) => {
                if (!cancelled) setSurvey(response.result);
            })
            .catch(() => {
                // 실패 시 참여 줄만 — 메뉴 자체는 정상 동작해야 한다
                if (!cancelled) setSurvey(null);
            });
        return () => {
            cancelled = true;
        };
    }, [loadCurrent]);

    useEffect(() => {
        // 상위 % 는 응답 + 면허 인증이 모두 갖춰졌을 때만 의미가 있다(그 외엔 200 eligible:false 또는 404)
        if (!loadCurrent || !canLoadBaseline || baselineRequested.current) return;
        if (!respondedSalaryThisYear(survey)) return;
        baselineRequested.current = true;
        (async () => {
            try {
                const mine = await api.salarySurvey.getMyResult();
                if (!isSalaryUnavailable(mine.result) && 'percentileTop' in mine.result) {
                    setPercentileTop(mine.result.percentileTop);
                    return;
                }
            } catch {
                // 올해 결과가 아직 없으면(발행 전·그룹 부족 404) 작년 조사 기준으로 넘어간다
            }
            try {
                const base = await api.salarySurvey.getMyBaseline();
                setPercentileTop(isSalaryUnavailable(base.result) ? null : base.result.percentileTop);
            } catch {
                setPercentileTop(null);
            }
        })();
    }, [loadCurrent, canLoadBaseline, survey]);

    return { survey, links: buildSalaryEntryLinks(survey, percentileTop) };
};
